import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { getFx } from "./api";

const CurrencyContext = createContext(null);

const CURRENCIES = {
    USD: { symbol: "$", code: "USD", label: "Dollaro USA" },
    EUR: { symbol: "€", code: "EUR", label: "Euro" },
    DKK: { symbol: "kr", code: "DKK", label: "Corona Danese" },
};

// NOTE: localStorage only stores the user's display currency preference (USD/EUR/DKK code).
// No sensitive data is persisted. The same applies to the search history in SearchBar.
const STORAGE_KEY = "aurum_currency";

export const CurrencyProvider = ({ children }) => {
    const [currency, setCurrencyState] = useState(() => {
        const saved = typeof window !== "undefined" ? window.localStorage.getItem(STORAGE_KEY) : null;
        return CURRENCIES[saved] ? saved : "USD";
    });
    const [rates, setRates] = useState({ USD: 1, EUR: 0.92, DKK: 6.85 });

    useEffect(() => {
        getFx().then(setRates).catch(() => {});
    }, []);

    const setCurrency = useCallback((c) => {
        if (CURRENCIES[c]) {
            setCurrencyState(c);
            window.localStorage.setItem(STORAGE_KEY, c);
        }
    }, []);

    const toUsd = useCallback((amountInCurrency) => {
        // Convert from displayed currency back to USD (for form inputs)
        const r = rates[currency] || 1;
        return amountInCurrency / r;
    }, [rates, currency]);

    const toCurrency = useCallback((amountInUsd) => {
        const r = rates[currency] || 1;
        return amountInUsd * r;
    }, [rates, currency]);

    const format = useCallback((amountInUsd, opts = {}) => {
        if (amountInUsd == null || isNaN(amountInUsd)) return "—";
        const converted = toCurrency(amountInUsd);
        const { decimals = 2, withSymbol = true } = opts;
        const num = converted.toLocaleString("en-US", {
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals,
        });
        if (!withSymbol) return num;
        const { symbol } = CURRENCIES[currency];
        // EUR/USD prefix, DKK suffix (Danish convention)
        return currency === "DKK" ? `${num} ${symbol}` : `${symbol}${num}`;
    }, [currency, toCurrency]);

    const value = useMemo(() => ({
        currency,
        setCurrency,
        rates,
        currencies: CURRENCIES,
        format,
        toUsd,
        toCurrency,
        symbol: CURRENCIES[currency].symbol,
    }), [currency, setCurrency, rates, format, toUsd, toCurrency]);

    return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
};

export const useCurrency = () => {
    const ctx = useContext(CurrencyContext);
    if (!ctx) throw new Error("useCurrency must be used inside CurrencyProvider");
    return ctx;
};
